import { ReferenceHierarchyResponse, ReferenceRow, RollupRow, TreeNode } from './index';
import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referenceAPI } from 'api/reference';

function convertReferenceRowIntoTreeData(row: ReferenceRow): TreeNode {
    return {
        id: row.external_report_reference_id,
        children: [],
        name: row.name,
        data: {
            namePath: [row.name],
            linked_ref_id: 0,
            collection_id: row?.collection_id ?? 0,
            parent: row.external_report_rollup_id ?? 0,
            type: 'reference',
            level: 0,
            searchField: row.name.toLowerCase(),
            fakeRollupId: (row?.linked_parent_id ?? 0 > 0) ? 'Y' : 'N',
        },
    };
}
function convertRollupRowIntoTreeData(row: RollupRow): TreeNode {
    return {
        id: row.external_report_rollup_id,
        children: [],
        name: row.name,
        data: {
            namePath: [row.name],
            collection_id: 0,
            linked_ref_id: row.linked_ref_id ?? 0,
            parent: row.parent_external_report_rollup_id ?? 0,
            type: 'rollup',
            level: row.level,
            searchField: row.name.toLowerCase(),
            fakeRollupId: row.fake_rollup_id ?? 'N',
        },
    };
}
export default function useReferenceHierarchy(profileId: number, selectedReferenceId: number, capability?: string) {
    const {
        data: infoRequestData,
        isSuccess: isInfoRequestSuccess,
        refetch: refetchInfoRequest,
    } = useQuery<ReferenceHierarchyResponse, Error>({
        queryKey: ['reference_data', profileId],
        queryFn: () => referenceAPI.getReferenceHierarchy(profileId, capability),
    });

    const referencesList = useRef<Array<TreeNode>>([]);
    const rollupsList = useRef<Array<TreeNode>>([]);
    // Tree data
    const nodesList = useMemo(() => {
        referencesList.current = infoRequestData
            ? infoRequestData.references.map((row) => convertReferenceRowIntoTreeData(row))
            : [];
        rollupsList.current = infoRequestData
            ? infoRequestData.rollups.map((row) => convertRollupRowIntoTreeData(row))
            : [];

        // Build hash map for Nodes
        const listMap: Record<string, TreeNode> = {};
        rollupsList.current.forEach((node) => {
            listMap['rollup_' + node.id] = node;
        });
        referencesList.current.forEach((node) => {
            if (selectedReferenceId == node.id) {
                // Mark as open
                node.state = { expanded: true };
            }
            listMap['ref_' + node.id] = node;
        });

        // Build tree of Nodes from list
        for (const [index, node] of Object.entries(listMap)) {
            if (node?.data?.parent) {
                const key = 'rollup_' + node.data.parent;
                if (typeof listMap[key]?.children == 'undefined') {
                    continue;
                }
                // @ts-ignore
                listMap[key].children.push(node);
                // // If child is open, also open parent
                // if (node?.state?.expanded) {
                //     listMap[key].state = { expanded: true };
                // }
            }
        }

        const expandParent = (node: TreeNode) => {
            node.state = { expanded: true };
            if (node.data?.parent && listMap['rollup_' + node.data.parent]) {
                expandParent(listMap['rollup_' + node.data.parent]);
            }
        };
        if (listMap['ref_' + selectedReferenceId]) {
            expandParent(listMap['ref_' + selectedReferenceId]);
        }
        const buildNamePath = (node: TreeNode, savePath: boolean): Array<string> => {
            if (node.data?.parent && listMap['rollup_' + node.data.parent]) {
                const parent = listMap['rollup_' + node.data.parent];
                const namePath = buildNamePath(parent, false).concat(node.name);
                if (savePath) {
                    node.data.namePath = namePath;
                }
                return namePath;
            }
            return [node.name];
        };

        const result = Object.values(listMap);
        result.forEach((node) => buildNamePath(node, true));

        return result.filter((node: TreeNode) => node?.data?.parent == 0);
    }, [infoRequestData, selectedReferenceId]);
    return { nodesList, infoRequestData, isInfoRequestSuccess, refetchInfoRequest, referencesList, rollupsList };
}
